import React from 'react'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { BreadcrumbJsonLd, LocalBusinessJsonLd, NextSeo } from 'next-seo'
import { useDispatch, useSelector } from 'react-redux'
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle'
import { FiShoppingBag } from '@react-icons/all-files/fi/FiShoppingBag'
import { wrapper } from '../../store/configStore'
import { SERVER_IP } from '../../store/middleware/api'
import { getFilteredProducts, productsReceived, productsSetFilters } from '../../store/slices/entities/products'
import { getActiveUser } from '../../store/slices/user'
import { SHOW_POPUP } from '../../store/slices/ui'
import { BUSINESS_HOURS } from '../../content/businessHours'
import AudioClosingActionBand from '../../components/design-system/marketing/AudioClosingActionBand'
import CatalogHelp from '../../components/design-system/commerce/CatalogHelp'
import Container from '../../components/design-system/foundations/Container'
import EquipmentGuide from '../../components/design-system/marketing/EquipmentGuide'
import EventShowcase from '../../components/design-system/marketing/EventShowcase'
import FAQGroup from '../../components/design-system/marketing/FAQGroup'
import MediaFeature from '../../components/design-system/marketing/MediaFeature'
import PageShell from '../../components/design-system/foundations/PageShell'
import ProductCatalog from '../../components/design-system/commerce/ProductCatalog'
import RelatedServices from '../../components/design-system/marketing/RelatedServices'
import ReviewProof from '../../components/design-system/marketing/ReviewProof'
import ServiceCategoryGrid from '../../components/design-system/marketing/ServiceCategoryGrid'
import ServiceHero from '../../components/design-system/marketing/ServiceHero'
import ServiceIntroduction from '../../components/design-system/marketing/ServiceIntroduction'
import QuotePop from '../../components/DeferredQuotePopup'
import * as gtag from '../../lib/gtag'
import { compactCatalogProducts } from '../../utilities/catalogPublicPayload'
import { compactPublicEvents } from '../../utilities/eventPublicPayload'
import { getAudioFamilyBreadcrumbs, toBreadcrumbJsonLdItems, toHeroBreadcrumbs } from '../../utilities/audioFamilyBreadcrumbs'
import { buildSpeakerRentalCatalogOffer, isSpeakerRentalCatalogProduct } from '../../utilities/speakerRental'
import { requestPageData } from '../../utilities/boundedPageData'
import orlandoSpeakerHero from '../../public/Pictures/categories-v2/orlando-speaker-rental-hero-v2.jpg'
import speakerPresentation from '../../public/Pictures/orlando-speaker-top.jpg'
import speakerEvent from '../../public/Pictures/Product/speaker-rental-miami.jpg'
import speakerGuide from '../../public/Pictures/Product/audio-rental-miami.jpg'
import poweredSpeaker from '../../public/Pictures/speakers-a.jpg'
import subwoofer from '../../public/Pictures/subwoofer_v.jpg'

const SeoPageDrawer = dynamic(() => import('../../components/SeoPageDrawer'), { ssr: false })

const PAGE_PATH = '/orlando/speaker-rental'
const CANONICAL = `https://miamisoundrental.com${PAGE_PATH}`
const TITLE = 'Speaker Rental in Orlando & Central Florida | Miami Sound Rental'
const DESCRIPTION = 'Rent powered speakers, speaker pairs and subwoofer bundles from our Orlando branch, with pickup or delivery and setup across Orlando, Tampa and Central Florida.'
const ORLANDO_REVIEWS_URL = 'https://www.google.com/maps/search/?api=1&query=Miami+Sound+Rental+Orlando+FL'

const fallbackQuestions = [
  {
    question: 'Can I pick up speakers from your Orlando warehouse?',
    answer: 'Yes, eligible speaker rentals can be picked up from our Orlando branch at 8600 Commodity Circle. Choose your dates first to see current Orlando availability and the fulfillment choices available for each item.'
  },
  {
    question: 'Do you deliver and set up speakers in Orlando?',
    answer: 'Yes. Delivery and setup are available across Orlando and Central Florida. Enter the event dates and location to receive the applicable delivery, setup, and collection price before checkout.'
  },
  {
    question: 'Can you deliver speaker rentals to Tampa?',
    answer: 'Yes. Tampa speaker rentals are fulfilled by our Orlando team when the dates, travel window, and equipment are available. Enter the Tampa venue when starting the rental so the correct fulfillment and travel pricing can be confirmed.'
  },
  {
    question: 'Is same-day speaker rental available?',
    answer: 'Same-day service may be available when the equipment and team are available. Online bookings require at least four hours of notice, including two weekday business hours. Call 407 807 0052 for shorter-notice requests.'
  },
  {
    question: 'Should I rent one speaker or a speaker pair?',
    answer: 'One powered speaker can cover a small speech or focused listening area. A pair provides broader, more even coverage and stereo playback for presentations, parties, and larger rooms.'
  },
  {
    question: 'When should I add a subwoofer?',
    answer: 'Add a subwoofer for DJs, dancing, live music, or programs where low-frequency impact matters. Speech-focused events usually do not need one.'
  },
  {
    question: 'Is this page for a complete PA system?',
    answer: 'This page is for individual powered speakers, speaker pairs, portable speaker systems, and straightforward speaker-and-subwoofer bundles. For multiple zones, live sound, line arrays, or a complete custom PA, use our Sound System Rental service.'
  }
]

const normalizeRelatedCategories = subCategory => ({
  ...(subCategory || {}),
  similar_categories: (subCategory?.similar_categories || []).map(service => {
    const path = String(service.url || '').replace('https://miamisoundrental.com', '')
    if (path === '/karaoke-rental') return { ...service, url: '/orlando/karaoke-rental' }
    return service
  })
})

export const getStaticProps = wrapper.getStaticProps(async ({ store }) => {
  const [landingResponse, subCategoryResponse, productResponse, reviewResponse] = await Promise.all([
    requestPageData({
      url: `${SERVER_IP}/user/static-landing`,
      fallback: {
        newsletterevents: [],
        total_events: null
      },
      label: `landing proof for ${PAGE_PATH}`
    }),
    requestPageData({
      url: `${SERVER_IP}/subcategory/name/main-speakers`,
      params: {
        cat: 'sound'
      },
      fallback: {
        similar_categories: []
      },
      label: `related categories for ${PAGE_PATH}`
    }),
    requestPageData({
      required: true,
      validateData: Array.isArray,
      url: `${SERVER_IP}/inventory/product`,
      params: {
        msr: true,
        cat: 'main_speakers'
      },
      label: `products for ${PAGE_PATH}`
    }),
    requestPageData({
      url: `${SERVER_IP}/google-review/public`,
      params: {
        page: PAGE_PATH,
        limit: 3
      },
      fallback: null,
      label: `reviews for ${PAGE_PATH}`
    })
  ])

  const products = Array.isArray(productResponse.data) ? productResponse.data : []
  store.dispatch({ type: productsReceived.type, payload: compactCatalogProducts(products) })
  store.dispatch({ type: productsSetFilters.type, payload: { static: true } })

  const { newsletterevents = [], total_events = null } = landingResponse.data || {}
  const usedFallback = [landingResponse, subCategoryResponse, productResponse, reviewResponse]
    .some(response => response.usedFallback)

  return {
    props: {
      newsletterevents: compactPublicEvents(newsletterevents),
      total_events,
      subCate: normalizeRelatedCategories(subCategoryResponse.data),
      reviewData: reviewResponse.data
    },
    revalidate: usedFallback ? 60 : 3600
  }
})

const OrlandoSpeakerRental = ({ newsletterevents, total_events, subCate, reviewData }) => {
  const products = useSelector(getFilteredProducts)
  const user = useSelector(getActiveUser)
  const dispatch = useDispatch()
  const breadcrumbs = getAudioFamilyBreadcrumbs(PAGE_PATH)
  const speakerProducts = products.filter(isSpeakerRentalCatalogProduct)
  const localSchemaProducts = speakerProducts.filter(product => product.orlando)

  const openQuote = () => {
    gtag.event({ action: 'quote_click', label: 'leads', value: 3, category: 'orders' })
    dispatch(SHOW_POPUP({ key: 'quote', size: 'medium', body: <QuotePop /> }))
  }

  const trackCall = () => gtag.event({
    action: 'booking_assistance_call_CTA',
    label: 'leads',
    value: 2,
    category: 'orders'
  })

  const primaryAction = {
    label: 'Browse Orlando Speakers',
    href: '#catalog',
    icon: FiShoppingBag,
    conversionKey: 'orlando-speaker-rental.hero.browse-speakers',
    channel: 'rental',
    salesPath: 'self_service',
    onClick: () => gtag.event({ action: 'catalog_click', label: 'leads', value: 2, category: 'orders' })
  }

  const secondaryAction = {
    label: 'Get Speaker Help',
    icon: FiMessageCircle,
    conversionKey: 'orlando-speaker-rental.hero.ask-specialist',
    channel: 'quote',
    salesPath: 'managed',
    onClick: openQuote
  }

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${CANONICAL}#service`,
    name: 'Speaker Rental in Orlando and Central Florida',
    serviceType: 'Powered speaker, speaker pair, portable speaker system, and subwoofer bundle rental',
    url: CANONICAL,
    description: DESCRIPTION,
    provider: { '@id': 'https://miamisoundrental.com/orlando#localbusiness' },
    areaServed: [
      { '@type': 'City', name: 'Orlando' },
      { '@type': 'City', name: 'Tampa' },
      { '@type': 'AdministrativeArea', name: 'Central Florida' }
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Orlando speaker rental options',
      itemListElement: (localSchemaProducts.length ? localSchemaProducts : speakerProducts)
        .slice(0, 12)
        .map(buildSpeakerRentalCatalogOffer)
    }
  }

  const useCases = [
    {
      eyebrow: 'Speech and presentations',
      title: 'Powered speakers for clear spoken audio',
      summary: 'A straightforward path for meetings, ceremonies, presentations, and announcements.',
      body: <p>Choose one speaker for a focused area or a pair for wider, more even coverage. If the program also needs microphones, use the compatible microphone-and-speaker package path.</p>,
      image: poweredSpeaker,
      imageAlt: 'Powered loudspeaker prepared for an Orlando presentation',
      href: '/sound/microphone-and-speaker',
      actionLabel: 'View Microphone & Speaker Packages'
    },
    {
      eyebrow: 'DJs and celebrations',
      title: 'Speaker and subwoofer packages for music',
      summary: 'Add dedicated bass when dancing, DJ playback, or live music needs more low-frequency impact.',
      body: <p>Compare powered speakers first, then add a subwoofer or choose a ready-made speaker-and-bass bundle sized for the room and audience.</p>,
      image: subwoofer,
      imageAlt: 'Professional subwoofer used with powered speakers for event music',
      href: '/sound/subwoofer',
      actionLabel: 'Compare Subwoofer Options'
    },
    {
      eyebrow: 'Larger or more complex events',
      title: 'Move to complete PA planning when needed',
      summary: 'Multiple rooms, live performers, line arrays, mixing, and technical operation require a different service path.',
      body: <p>Use the complete Sound System Rental page when the event needs system design rather than a straightforward speaker rental.</p>,
      image: speakerPresentation,
      imageAlt: 'Professional speaker system supporting a larger Central Florida event',
      href: '/sound-system-rental',
      actionLabel: 'Plan a Complete Sound System'
    }
  ]

  return <>
    <BreadcrumbJsonLd itemListElements={toBreadcrumbJsonLdItems(breadcrumbs)} />
    <NextSeo
      title={TITLE}
      description={DESCRIPTION}
      canonical={CANONICAL}
      twitter={{
        handle: '@miamisoundrent1',
        site: '@miamisoundrent1',
        cardType: 'summary_large_image'
      }}
      openGraph={{
        type: 'website',
        url: CANONICAL,
        title: TITLE,
        description: DESCRIPTION,
        images: [{
          url: 'https://miamisoundrental.com/Pictures/categories-v2/orlando-speaker-rental-hero-v2.jpg',
          width: 1672,
          height: 941,
          alt: 'Powered speaker supporting an Orlando event presentation'
        }],
        site_name: 'Miami Sound Rental'
      }}
      additionalMetaTags={[{
        name: 'keywords',
        content: 'speaker rental Orlando, Orlando speaker rental, rent speakers Orlando, powered speaker rental, speaker rental Tampa, Central Florida speaker rental'
      }]}
    />
    <LocalBusinessJsonLd
      type='Store'
      id='https://miamisoundrental.com/orlando#localbusiness'
      name='Miami Sound Rental Orlando'
      description='Audio visual equipment rental and event production services from our Orlando branch.'
      url='https://miamisoundrental.com/orlando'
      telephone='+14078070052'
      address={{
        streetAddress: '8600 Commodity Cir #152',
        addressLocality: 'Orlando',
        addressRegion: 'FL',
        postalCode: '32819',
        addressCountry: 'US'
      }}
      geo={{ latitude: '28.445134', longitude: '-81.431191' }}
      images={['https://miamisoundrental.com/Pictures/categories-v2/orlando-speaker-rental-hero-v2.jpg']}
      openingHours={BUSINESS_HOURS}
      areaServed={[{
        geoMidpoint: { latitude: '28.445134', longitude: '-81.431191' },
        geoRadius: '160000'
      }]}
    />
    <Head>
      <script
        key='orlando-speaker-rental-service-schema'
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
    </Head>
    <PageShell>
      <ServiceHero
        breadcrumbs={toHeroBreadcrumbs(breadcrumbs)}
        eyebrow='Speaker rental · Orlando, Tampa & Central Florida'
        title='Speaker Rental in Orlando'
        description='Compare powered speakers, speaker pairs, portable systems, and speaker-and-subwoofer bundles. See Orlando inventory first, then choose warehouse pickup or delivery and setup.'
        image={{ src: orlandoSpeakerHero, position: '90% center' }}
        imageAlt='Powered speaker supporting a corporate presentation in an Orlando event room'
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        stackActionsOnMobile
        processLabel='How speaker rental works'
        steps={[
          { icon: 'search', title: 'Compare speakers', description: 'Start with Orlando inventory.' },
          { icon: 'calendar', title: 'Choose dates', description: 'Check current availability.' },
          { icon: 'truck', title: 'Select fulfillment', description: 'Pickup or delivery and setup.' },
          { icon: 'success', title: 'Confirm online', description: 'Review the final price first.' }
        ]}
      />

      <ServiceIntroduction
        eyebrow='Local Orlando speaker inventory'
        heading='Choose a speaker rental by coverage, program, and fulfillment'
        body={<>
          <p>Start with the audience and what they need to hear. Powered speakers work well for speeches, presentations, background music, parties, and straightforward event audio. Speaker pairs widen coverage; subwoofer bundles add low-frequency impact for DJs and dancing.</p>
          <p>The catalog opens with only equipment stocked by our Orlando branch. Select <strong>All</strong> if you also want to compare options that may be fulfilled from Miami when dates and travel allow; those products are identified before you commit to the rental.</p>
        </>}
        guideHeading='Orlando pickup or Central Florida delivery?'
        guideSummary='Use the fulfillment option that matches the event, equipment, and support required.'
        guideBody={<>
          <p>Eligible equipment can be picked up from our Orlando warehouse. Delivery and setup are available across Orlando and Central Florida, including Tampa when the travel window and equipment are available.</p>
          <p>Enter the event dates and location to see real availability and the applicable fulfillment price. Short-notice and same-day requests are conditional—call <a href='tel:+14078070052'>407 807 0052</a> when the online window is too short.</p>
        </>}
        image={speakerEvent}
        imageAlt='Professional powered speakers and subwoofers arranged around a DJ stage'
        signals={[
          'Orlando inventory is the default catalog view',
          'Live dates and fulfillment choices appear before checkout',
          'Tampa delivery is handled by the Orlando team when available'
        ]}
      />

      <ProductCatalog
        carousel={{
          name: 'Orlando Speaker Rentals',
          url: CANONICAL,
          description: 'Powered speakers, speaker pairs, portable systems, and speaker-and-subwoofer bundles for rent'
        }}
        filters={[
          ['Powered Speakers', 'cabinet'],
          ['Portable Speaker Systems', 'pla'],
          ['Speaker + Subwoofer Bundles', 'subtop']
        ]}
        sortOptions={['Wattage', 'Event Size', 'Sound Quality']}
        listName='Orlando Speaker Catalog'
        anchorId='catalog'
        eyebrow='Compare speaker options'
        description='Showing equipment stocked in Orlando. Select All only when you also want to compare Miami-fulfilled options.'
        products={speakerProducts}
        inventoryMarket='Orlando'
      />

      <Container>
        <CatalogHelp
          onQuote={openQuote}
          onCall={trackCall}
          phone='407 807 0052'
          phoneHref='tel:+14078070052'
        />
      </Container>

      <RelatedServices
        eyebrow='Choose the correct audio path'
        heading='Frequently rented together'
        description='Keep individual powered speakers here, or move to the specialist page when the event needs microphones, dedicated bass, a complete PA, line-array coverage, or karaoke.'
        subCategory={subCate}
        additionalServices={[
          {
            h1: 'Microphone & Speaker Packages',
            url: '/sound/microphone-and-speaker',
            miniText: 'Compare compatible microphones, speakers, stands, cables, and presentation bundles.'
          },
          {
            h1: 'Complete Sound System & PA Rental',
            url: '/sound-system-rental',
            miniText: 'Plan several speakers, multiple zones, live programs, mixing, setup, and technical operation.'
          },
          {
            h1: 'Subwoofer Rental',
            url: '/sound/subwoofer',
            miniText: 'Add dedicated bass for DJs, dancing, live music, and bass-heavy playback.'
          },
          {
            h1: 'Orlando Karaoke Rental',
            url: '/orlando/karaoke-rental',
            miniText: 'Choose a ready-to-use karaoke package with compatible speakers and microphones.'
          }
        ]}
      />

      <MediaFeature
        eyebrow='Speaker rental guide'
        heading='Powered speakers, pairs, and portable PA options'
        body={<>
          <p>A powered speaker includes its own amplifier and is the simplest starting point for most speeches, presentations, and music playback. A speaker pair covers a wider audience more evenly. Portable column systems can be convenient for smaller programs that need simple inputs and an unobtrusive footprint.</p>
          <p>For music-driven events, a dedicated subwoofer adds bass that a full-range speaker cannot reproduce with the same impact. For several rooms, live bands, complex mixing, or engineered coverage, move to the complete PA planning path.</p>
        </>}
        secondaryHeading='Check the source connection before reserving'
        secondaryBody={<p>Professional speakers commonly use XLR or TRS inputs. A phone, laptop, DJ controller, microphone receiver, or presentation computer may need a mixer, adapter, or compatible cable. The product details and package contents show what is included; ask an audio specialist when the source is uncertain.</p>}
        image={speakerGuide}
        imageAlt='Four professional powered speakers positioned around a DJ stage'
        reverse
      />

      <EquipmentGuide title='Choose the Correct Orlando Speaker Rental Path' items={useCases} />
      <FAQGroup
        questions={fallbackQuestions}
        title='Orlando Speaker Rental Questions'
        intro='Answers about Orlando inventory, Central Florida and Tampa delivery, timing, speaker selection, and complete PA systems.'
      />
      <ServiceCategoryGrid
        orlando
        currentCategory='sound'
        description='Explore the equipment categories with dedicated Orlando pages, or use the shared specialist pages for services fulfilled across Florida.'
      />
      <EventShowcase
        eyebrow='Company-wide recent production work'
        heading='Speakers working in real event environments'
        description='These documented Miami Sound Rental projects show the kinds of rooms, audiences, and programs our teams support; they are not presented as Orlando-only events.'
        filter={['party', 'corporate', 'conference', 'concert', 'private', 'presentation']}
        events={newsletterevents}
        totalEvents={total_events}
      />
      <ReviewProof
        filter={['party', 'speaker']}
        heading='Recent feedback relevant to speakers and sound'
        description='Showing verified Google feedback selected for speaker, sound, event-service, and Orlando relevance when available.'
        initialData={reviewData}
        pagePath={PAGE_PATH}
        reviewsUrl={ORLANDO_REVIEWS_URL}
      />
      <AudioClosingActionBand
        pagePath={PAGE_PATH}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
      />
    </PageShell>
    {user?.email === 'pedro@drynwet.com' && user?.employee && (
      <SeoPageDrawer pagePath={PAGE_PATH} />
    )}
  </>
}

export default OrlandoSpeakerRental
